import { useEffect, useState } from "react";
import { webComponentWithJsonProp } from "../services/webComponentService";
import { ImageGalleryButtonsProps } from "../types/components/ImageGalleryButtonsProps";
import { ResponsiveImage } from "../types/components/ResponsiveImage";
import { Picture } from "./Picture.tsx";

function ImageGalleryButtons({ images, imageGalleryId }: ImageGalleryButtonsProps) {
	const [selectedIndex, setSelectedIndex] = useState(0);

	useEffect(() => {
		const onExternalChange = (e: CustomEvent) => {
			if (e?.detail?.galleryId !== imageGalleryId) {
				return;
			}
			setSelectedIndex(e.detail.index);
		};
		window.addEventListener("image-gallery:change", onExternalChange as EventListener);
		return () => window.removeEventListener("image-gallery:change", onExternalChange as EventListener);
	}, [imageGalleryId]);

	function onClick(image: ResponsiveImage, index: number) {
		setSelectedIndex(index);

		const nodes = [...document.querySelectorAll(`[data-image-gallery="${imageGalleryId}"]`)];
		for (const node of nodes) {
			node.classList.add("hidden");
		}
		const selectedNode = nodes.find((x) => x.querySelector("img")?.getAttribute("src") === image.urls[0]);
		if (selectedNode) {
			selectedNode.classList.remove("hidden");
		}

		window.dispatchEvent(
			new CustomEvent("image-gallery:change", {
				detail: { galleryId: imageGalleryId, index },
			})
		);
	}

	return (
		<div
			className="flex w-full flex-nowrap gap-2 overflow-x-auto md:flex-nowrap md:gap-6 md:overflow-x-auto [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-track]:bg-transparent"
			aria-orientation="horizontal"
			role="tablist"
		>
			{images.map((image: ResponsiveImage, index: number) => (
				<button
					key={index}
					onClick={() => onClick(image, index)}
					className="group relative h-[60px] w-[84px] shrink-0 cursor-pointer items-center justify-center rounded-md bg-white text-sm font-medium text-gray-900 uppercase hover:bg-gray-50 focus:ring focus:ring-indigo-500/50 focus:ring-offset-4 focus:outline-none md:h-28 md:w-40 md:shrink-0"
					aria-controls="tabs-1-panel-1"
					aria-selected={selectedIndex === index}
					role="tab"
					type="button"
				>
					<span
						className={
							`absolute inset-0 overflow-hidden rounded-md transition-opacity ` +
							(selectedIndex === index
								? "border-2 [border-color:var(--color-complementary)]"
								: "border [border-color:var(--color-border)] opacity-50")
						}
					>
						<Picture imageUrls={image.urls} altText={image.alt} imgClassName="size-full object-cover" />
					</span>
					<span
						className="pointer-events-none absolute inset-0 rounded-md ring-2 ring-transparent ring-offset-2"
						aria-hidden="true"
					/>
				</button>
			))}
		</div>
	);
}

export default webComponentWithJsonProp("image-gallery-buttons", ImageGalleryButtons);
